import { useAuthState } from '@kaa/auth/common';
import { RouteActive } from '@kaa/common/types';
import { i18nKeys } from '@kaa/i18n/providers/keys';
import {
  Icon,
  PluxeeLogoLink,
  SwColumn,
  SwGrid,
  SwIcon,
  SwLayout,
  SwLink,
} from '@kaa/ui-flanders/components';
import React, { AllHTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dataTest } from '../../datatest/keys';
import { getRoute, Routes } from '../../routes';
import { AuthContext } from '../../types';
import {
  EventAction,
  sendMainNavigationEventToGTM,
} from '../../utils/google-analytics';
import './WlFunctionalHeader.style.scss';
import { WlFunctionHeaderTab } from './WlFunctionalHeader.types';
import { getTabs, isActiveTab } from './WlFunctionalHeader.utils';
import { WlFunctionalHeaderSupportMobile } from './WlFunctionalHeaderSupportMobile';

type FunctionalHeaderProps = AllHTMLAttributes<HTMLDivElement> & {
  activeRoute?: RouteActive<Routes> | null;
  closeNav?: () => void;
  className?: string;
};

export const WlFunctionalHeader = ({
  activeRoute,
  className = '',
  closeNav,
}: FunctionalHeaderProps) => {
  const { t } = useTranslation();
  const { logout } = useAuthState<AuthContext>();
  const { authUser } = useAuthState<AuthContext>();
  const [tabs, setTabs] = useState<WlFunctionHeaderTab[]>();

  useEffect(() => {
    if (activeRoute) {
      setTabs(getTabs(activeRoute.route.id, t));
    }
  }, [activeRoute, t]);

  if (!activeRoute) {
    return null;
  }

  return (
    <>
      <SwColumn className={`${className} site-nav`}>
        <h1 className="site-nav__logo">
          <PluxeeLogoLink imageClassName="site-nav__image" />
        </h1>
        <SwLayout>
          <SwGrid modVCenter>
            <SwColumn className="vl-nav">
              <SwGrid>
                <SwColumn>
                  <nav className="site-nav__navigation">
                    <SwColumn
                      className="site-nav__navigation-wrapper"
                      width="10"
                      widthS="12"
                    >
                      <ul>
                        {tabs?.map((tab) => {
                          const route = getRoute(tab.id, {
                            params: activeRoute.params,
                          });
                          return (
                            <li
                              key={`tab-${tab.id}`}
                              className={
                                isActiveTab(tab, activeRoute) ? 'active' : ''
                              }
                            >
                              <SwLink
                                to={route.path}
                                onClick={() => {
                                  sendMainNavigationEventToGTM(
                                    EventAction.CLICK,
                                    tab.id,
                                  );
                                }}
                              >
                                {tab.header}
                                {tab.children && (
                                  <SwIcon
                                    className="showMobile navItem-icon"
                                    icon={Icon.ARROW_DOWN}
                                  />
                                )}
                              </SwLink>
                              {tab.children && (
                                <ul className="site-nav__subnavigation">
                                  {tab.children.map((child) => {
                                    const tabChildRoute = getRoute(child.id, {
                                      params: activeRoute.params,
                                    });
                                    return (
                                      <li
                                        key={`tab-${tab.id}-child-${child.id}`}
                                        className={
                                          isActiveTab(child, activeRoute)
                                            ? 'active'
                                            : ''
                                        }
                                      >
                                        <SwLink
                                          onClick={() => {
                                            if (closeNav) {
                                              closeNav();
                                            }

                                            sendMainNavigationEventToGTM(
                                              tab.id,
                                              child.id,
                                            );
                                          }}
                                          to={tabChildRoute.path}
                                        >
                                          {child.header}
                                        </SwLink>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </li>
                          );
                        })}
                        <WlFunctionalHeaderSupportMobile />
                        {authUser && (
                          <li className="logout">
                            <SwLink
                              title={t(i18nKeys.navigation.logout)}
                              data-testid={dataTest.header.linkLogout}
                              onClick={() => {
                                sendMainNavigationEventToGTM(
                                  EventAction.CLICK,
                                  EventAction.LOG_OUT,
                                );
                                if (closeNav) {
                                  closeNav();
                                }
                                close();
                                logout();
                              }}
                            >
                              <SwIcon
                                className="showMobile navItem-icon"
                                icon={Icon.LOGOUT}
                              />
                              {t(i18nKeys.navigation.logout)}
                            </SwLink>
                          </li>
                        )}
                      </ul>
                    </SwColumn>
                  </nav>
                </SwColumn>
              </SwGrid>
            </SwColumn>
          </SwGrid>
        </SwLayout>
      </SwColumn>
    </>
  );
};
